import { useEffect, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { EmbeddedSheet } from "@/components/EmbeddedSheet";
import { emptyCells, newSheetId } from "@/lib/sheetEngine";
import api from "@/lib/api";
import { Link2, AlertTriangle } from "lucide-react";

function SheetNodeView({ node, updateAttributes, deleteNode, extension }) {
  const { sid, cells, refId, linked } = node.attrs;
  const [linkedCells, setLinkedCells] = useState(null);
  const [linkedTitle, setLinkedTitle] = useState("");
  const [linkErr, setLinkErr] = useState(false);
  const saveTimer = useRef(null);
  const onSaveTemplate = extension.options?.onSaveTemplate;

  useEffect(() => {
    if (!linked || !refId) return;
    let ignore = false;
    api.get(`/notes/${refId}`).then(({ data }) => {
      if (ignore) return;
      const c = data.sheet?.cells;
      if (Array.isArray(c) && c.length) {
        setLinkedCells(c);
        setLinkedTitle(data.title || "Hoja");
        updateAttributes({ cells: c });
      } else { setLinkErr(true); }
    }).catch(() => { if (!ignore) setLinkErr(true); });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linked, refId]);

  const handleChange = (_id, newCells) => {
    updateAttributes({ cells: newCells });
    if (linked && refId && !linkErr) {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        api.put(`/notes/${refId}`, { sheet: { cells: newCells } }).catch(() => {});
      }, 700);
    }
  };

  const wrapperProps = {
    className: "notacora-sheet-nodeview",
    contentEditable: false,
    onKeyDown: (e) => e.stopPropagation(),
    onMouseDown: (e) => e.stopPropagation(),
  };

  if (linked && refId) {
    if (linkErr) {
      return (
        <NodeViewWrapper {...wrapperProps}>
          <div className="not-prose my-4 rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4 flex items-center gap-3">
            <AlertTriangle size={18} className="text-amber-500 shrink-0" />
            <span className="text-sm text-amber-700 dark:text-amber-300">Hoja vinculada no disponible (fue eliminada o movida).</span>
            <button onClick={() => deleteNode()} className="ml-auto text-xs text-amber-700 dark:text-amber-300 underline">Quitar</button>
          </div>
        </NodeViewWrapper>
      );
    }
    if (linkedCells == null) {
      return (
        <NodeViewWrapper {...wrapperProps}>
          <div className="not-prose my-4 rounded-xl border border-slate-200 dark:border-slate-800 p-4 text-sm text-slate-400 font-mono2">Cargando hoja vinculada…</div>
        </NodeViewWrapper>
      );
    }
    return (
      <NodeViewWrapper {...wrapperProps}>
        <EmbeddedSheet
          raw={JSON.stringify({ id: sid, cells: linkedCells })}
          editable
          onChange={handleChange}
          onRemove={() => deleteNode()}
          badge={{ label: linkedTitle, href: `/hoja/${refId}` }}
        />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper {...wrapperProps}>
      <EmbeddedSheet
        raw={JSON.stringify({ id: sid, cells: cells && cells.length ? cells : emptyCells(4, 3) })}
        editable
        onChange={handleChange}
        onRemove={() => deleteNode()}
        onSaveTemplate={onSaveTemplate}
      />
    </NodeViewWrapper>
  );
}

export const NotacoraSheet = Node.create({
  name: "notacoraSheet",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { onSaveTemplate: null };
  },

  addAttributes() {
    return {
      sid: { default: null },
      refId: { default: null },
      linked: { default: false },
      cells: {
        default: null,
        parseHTML: (el) => {
          try { return JSON.parse(el.getAttribute("data-notacora-sheet")).cells; } catch { return null; }
        },
      },
    };
  },

  parseHTML() {
    return [{
      tag: "div[data-notacora-sheet]",
      getAttrs: (el) => {
        try {
          const o = JSON.parse(el.getAttribute("data-notacora-sheet"));
          return { sid: o.id, cells: o.cells, refId: o.refId || null, linked: !!o.linked };
        } catch { return {}; }
      },
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const payload = JSON.stringify({ id: node.attrs.sid, cells: node.attrs.cells, refId: node.attrs.refId, linked: node.attrs.linked });
    return ["div", mergeAttributes(HTMLAttributes, { "data-notacora-sheet": payload })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SheetNodeView);
  },

  addCommands() {
    return {
      insertSheet: (attrs = {}) => ({ commands, state }) =>
        commands.insertContentAt(state.selection.to, {
          type: this.name,
          attrs: {
            sid: attrs.sid || newSheetId(),
            cells: attrs.cells || emptyCells(4, 3),
            refId: attrs.refId || null,
            linked: !!attrs.linked,
          },
        }),
    };
  },
});
